import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { CustomerListItem } from '@rosita/shared';
import { SEO } from '../../seo/useSEO';
import { api } from '../../lib/api';
import { formatDate, formatEur } from '../../lib/format';
import { EditUserModal } from '../../components/admin/EditUserModal';

interface CustomerListResponse {
  items: CustomerListItem[];
  total: number;
  totalPages: number;
}

const inputClass =
  'rounded-md border border-admin-border bg-white px-3 py-2 text-sm focus:border-admin-primary focus:outline-none';
const secondaryBtn =
  'rounded-md border border-admin-border bg-white px-3 py-1.5 text-sm hover:bg-admin-surface-alt';
const thClass =
  'p-3 text-left text-xs font-semibold uppercase tracking-wider text-admin-muted';

export default function AdminClientesPage(): JSX.Element {
  const [sp, setSp] = useSearchParams();
  const [items, setItems] = useState<CustomerListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [editing, setEditing] = useState<CustomerListItem | null>(null);

  const load = async (): Promise<void> => {
    const params = new URLSearchParams(sp);
    params.set('scope', 'customers');
    if (!params.get('page')) params.set('page', '1');
    if (!params.get('limit')) params.set('limit', '25');
    const response = await api.get<CustomerListResponse>(
      `/admin/users?${params.toString()}`
    );
    setItems(response.data.items);
    setTotal(response.data.total);
  };

  useEffect(() => {
    void load();
  }, [sp]);

  const toggleActive = async (u: CustomerListItem): Promise<void> => {
    await api.patch(`/admin/users/${u.id}`, { isActive: !u.isActive });
    await load();
  };

  return (
    <>
      <SEO title="Clientes (admin)" noindex />
      <h1>Clientes ({total})</h1>
      <input
        type="search"
        placeholder="Pesquisar…"
        value={sp.get('search') ?? ''}
        onChange={(e) => {
          const next = new URLSearchParams(sp);
          if (e.target.value) next.set('search', e.target.value);
          else next.delete('search');
          next.set('page', '1');
          setSp(next);
        }}
        className={`${inputClass} my-4 w-80`}
      />
      <table className="overflow-hidden rounded-lg bg-admin-surface shadow-sm">
        <thead>
          <tr className="border-b border-admin-border">
            <th className={thClass}>Email</th>
            <th className={thClass}>Nome</th>
            <th className={thClass}>Nº encomendas</th>
            <th className={thClass}>Total gasto</th>
            <th className={thClass}>Última encomenda</th>
            <th className={thClass}>Criado</th>
            <th className={thClass}></th>
          </tr>
        </thead>
        <tbody>
          {items.map((u) => (
            <tr
              key={u.id}
              className="border-b border-admin-border last:border-b-0 hover:bg-admin-surface-alt"
            >
              <td className="p-3 font-medium">{u.email}</td>
              <td className="p-3">
                {u.firstName} {u.lastName}
              </td>
              <td className="p-3">{u.ordersCount}</td>
              <td className="p-3">{formatEur(u.totalSpent)}</td>
              <td className="p-3 text-admin-text-soft">
                {formatDate(u.lastOrderAt)}
              </td>
              <td className="p-3 text-admin-text-soft">
                {formatDate(u.createdAt)}
              </td>
              <td className="p-3 text-right">
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setEditing(u)}
                    className={secondaryBtn}
                  >
                    Editar
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      void toggleActive(u);
                    }}
                    className={secondaryBtn}
                  >
                    {u.isActive ? 'Desativar' : 'Ativar'}
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {editing ? (
        <EditUserModal
          user={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
        />
      ) : null}
    </>
  );
}
